import { place, type RoomContext, type RoomDecorator } from '../index';
import * as kit from '../../castle-kit';

/**
 * SCRIPTORIUM (Скрипторий) — the scribes' hall of Castle Hearthhold: where the guild's lore, ledgers and
 * illuminated charters are copied by candlelight. Composition is deliberately quiet so the work reads:
 * a double row of slanted SCRIBE DESKS (the work seats) faces the interior, and the single HERO is the
 * great brass CANDELABRA at room-centre — kept the ONLY bright focused light pool so the eye lands there.
 * Pigeonhole SCROLL SHELVES sit on the long walls between the desk rows; a "scene" TAPESTRY warms the
 * north wall; a LECTERN of finished work + a BOOKCASE anchor the south bay; two structural PILLARS frame
 * the centre. Lighting and banners were thinned (was 4 torches + 4 banners + a votive) to one torch and
 * one parchment banner per long wall, so nothing rivals the candelabra — cool, hushed, ink-and-vellum focus.
 *
 * Geometry: zone (2800,760) 380×420 → centre (2990,970). Doors on N (x2990), W (y970), E (y970) and S
 * (x2990) are kept clear, and the central cross (the x2990 / y970 lanes) is left walkable.
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { art } = ctx;
  const cx = 2990;
  const cy = 970;

  // ── Floor runner down the central work aisle (laid first, under everything) — a narrow E–W vellum band
  //    tying the two desk rows together without blocking the walkable cross. ──
  place(ctx, kit.runnerRug(art, 250, 56), cx, cy);

  // ── HERO: the great CANDELABRA dead-centre — the room's single bright focused light pool, unrivalled. ──
  place(ctx, kit.candelabra(art), cx, cy);

  // ── The two rows of SCRIBE DESKS (these ARE the work seats — a duck sits at each, facing up to its
  //    slanted, parchment-laden top). Brief anchors: top row y860, bottom row y1080. ──
  for (const sx of [2880, 2990, 3100]) {
    place(ctx, kit.scribeDesk(art), sx, 860); // top row
    place(ctx, kit.scribeDesk(art), sx, 1080); // bottom row
  }

  // ── Pigeonhole SCROLL SHELVES against the long walls between the desk rows (brief anchors) — the room's
  //    repeating motif, so neither side wall reads bare. ──
  place(ctx, kit.scrollShelf(art, 64, 76), 2840, 860); // W wall, top
  place(ctx, kit.scrollShelf(art, 64, 76), 3140, 860); // E wall, top
  place(ctx, kit.scrollShelf(art, 64, 76), 2840, 1080); // W wall, bottom
  place(ctx, kit.scrollShelf(art, 64, 76), 3140, 1080); // E wall, bottom

  // ── A LECTERN of finished work + a tall BOOKCASE of bound volumes anchor the southern bay (kept off the
  //    S door mouth at x2990) so it isn't a brown void. ──
  place(ctx, kit.lectern(art), 2870, 1145);
  place(ctx, kit.bookcase(art, 108), 3110, 1150);

  // ── A "scene" TAPESTRY alone warms the NORTH wall (off the N door mouth) — no second light beside it. ──
  place(ctx, kit.tapestry(art, 70, 100, 'scene'), 2990, 800);

  // ── ONE hanging parchment BANNER per long wall (blue west, neutral east) for a quiet vertical accent. ──
  place(ctx, kit.banner(art, 0x2f4a8a, 70), 2818, 970);
  place(ctx, kit.banner(art, undefined, 70), 3162, 970);

  // ── ONE wall TORCH per long wall for cool edge-light that never competes with the central candelabra
  //    (clear of the W/E door mouths at y970). ──
  place(ctx, kit.wallTorch(art), 2810, 860);
  place(ctx, kit.wallTorch(art), 3170, 1080);

  // ── Two floor PILLARS frame the central candelabra without crowding the cross lanes — colonnade feel. ──
  place(ctx, kit.pillar(art), 2890, 970);
  place(ctx, kit.pillar(art), 3090, 970);
};

export default decorate;
